import { $Mutable, $Unwrapped } from '#library/symbol.library.js';
import { secure } from '#library/proxy.library.js';
import { isReference, isUndefined, isString, isSymbol, isCallable } from '#library/assertion.library.js';
import { hasOwn, hasKey } from '#library/primitive.library.js';
import { registerSerializable } from '#library/serialize.library.js';
import { registerType, getSafeTag } from '#library/type.library.js';
/**
 * Some interesting Class Decorators
 */
/**
 * Determines a class name from its string tag, decorator context name, or constructor name.
 *
 * @param value - The class whose name should be determined
 * @param contextName - An optional name from the decorator context
 * @returns The resolved class name, or `undefined` when no name is available
 * @internal
 */
function getClassName(value, contextName) {
    return getSafeTag(value) ?? (isUndefined(contextName) ? (value.name || undefined) : String(contextName));
}
/**
 * Creates a class wrapper that applies an immutability strategy to each instance and hardens its static and prototype members.
 *
 * @param value - The class to wrap
 * @param name - The class name used for type registration, when available
 * @param addInitializer - Registers the initializer that hardens class members
 * @param immutabilityStrategy - Transforms each created instance
 * @param metadata - Optional decorator metadata used to determine mutable members
 * @returns The wrapped class, or the original class when subclass wrapping is incompatible with its private state
 * @internal
 */
function createImmutableWrapper(value, name, addInitializer, immutabilityStrategy, metadata) {
    const safeName = name || 'Anonymous';
    addInitializer(() => {
        const metaSymbol = Symbol.metadata;
        const meta = metadata ?? (metaSymbol ? value[metaSymbol] : undefined);
        const skip = [
            ...(value[$Mutable] ?? value.$Mutable ?? []),
            ...(value?.prototype?.[$Mutable] ?? value?.prototype?.$Mutable ?? []),
            ...(meta?.[$Mutable] ?? meta?.$Mutable ?? []),
        ];
        hardenClassStaticsAndPrototypes(value, value, skip);
    });
    // Classes with private static state (#field or transpiled WeakMap private fields) cannot be subclass-wrapped
    // without breaking ECMAScript private field brand checks. Return original constructor directly.
    const codeStr = isCallable(value) ? Function.prototype.toString.call(value) : '';
    const hasPrivateState = codeStr.includes('#') || codeStr.includes('WeakMap') || codeStr.includes('__private') || codeStr.includes('privateMap');
    const isRawConstructor = value[$Unwrapped] === true || value.raw === true || value[$Mutable] === 'unwrapped' || hasPrivateState;
    if (isRawConstructor) {
        if (name)
            registerType(value, name);
        return value;
    }
    const wrapper = {
        [safeName]: class extends value {
            constructor(...args) {
                super(...args);
                return immutabilityStrategy(this);
            }
        }
    }[safeName];
    if (name)
        registerType(wrapper, name);
    return wrapper;
}
/**
 * Determines if a member should be skipped during hardening based on the skip list.
 * Supports both simple string/symbol entries and conditional skip entries.
 *
 * @param name - The member name to check
 * @param skipList - Array of skip entries (strings, symbols, or conditional objects)
 * @returns True if the member should be skipped, false otherwise
 * @internal
 */
function shouldSkipMember(name, skipList) {
    if (!Array.isArray(skipList))
        return false;
    return skipList.some(entry => {
        if (isString(entry) || isSymbol(entry)) {
            return entry === name;
        }
        if (hasKey(entry, 'name')) {
            if (entry.name !== name)
                return false;
            const cond = entry.condition;
            return isCallable(cond) ? Boolean(cond()) : Boolean(cond ?? true);
        }
        return false;
    });
}
/**
 * Locks existing static and prototype members while allowing new members to be added.
 *
 * @internal
 */
function hardenClassStaticsAndPrototypes(value, wrapper, skip) {
    const lockStatic = (ctor) => {
        Reflect.ownKeys(ctor).forEach(name => {
            if (name === 'prototype' || name === 'length' || name === 'name' || name === 'constructor')
                return;
            if (shouldSkipMember(name, skip))
                return;
            const desc = Object.getOwnPropertyDescriptor(ctor, name);
            if (!desc)
                return;
            // Only lock if configurable or writable
            const update = {};
            if (desc.configurable)
                update.configurable = false;
            if ('writable' in desc && desc.writable)
                update.writable = false;
            if (Object.keys(update).length)
                Object.defineProperty(ctor, name, { ...desc, ...update });
        });
    };
    // Lock statics for both original and wrapper
    lockStatic(value);
    lockStatic(wrapper);
    // Lock down all existing prototype properties, but do NOT freeze the prototype object
    const lockPrototype = (proto) => {
        if (!isReference(proto))
            return;
        Reflect.ownKeys(proto).forEach(name => {
            if (name === 'constructor')
                return;
            if (shouldSkipMember(name, skip))
                return;
            const desc = Object.getOwnPropertyDescriptor(proto, name);
            if (!desc)
                return;
            const update = {};
            if (desc.configurable)
                update.configurable = false;
            if ('writable' in desc && desc.writable)
                update.writable = false;
            if (Object.keys(update).length)
                Object.defineProperty(proto, name, { ...desc, ...update });
        });
    };
    lockPrototype(value.prototype);
    lockPrototype(wrapper.prototype);
}
/**
 * A class decorator that secures a class instance with a mutation-throwing Proxy.
 * Provides "noisy immutability" by throwing an error if modifications are attempted.
 *
 * @remarks
 * **Workaround:** To protect against aggressive bundlers (Rollup/Terser) mutating the TS 7.0
 * ES2022 decorator IIFE structure, users must append `return Object.freeze(this) as this;`
 * (or the `secure` equivalent) to their constructors to ensure immutability survives production bundling.
 *
 * @param value - The class constructor to secure
 * @param context - The decorator context
 * @returns The secured class wrapper
 * @example
 * ```ts
 * @Securable
 * class Config { ... }
 * ```
 */
export function Securable(value, { kind, name, addInitializer, metadata }) {
    const finalName = getClassName(value, name);
    switch (kind) {
        case 'class':
            return createImmutableWrapper(value, finalName, addInitializer, secure, metadata);
        default:
            throw new Error(`@Securable decorating unknown 'kind': ${kind} (${name})`);
    }
}
/**
 * A class decorator that freezes a class instance to prevent modification.
 * Provides "silent immutability" by silently ignoring modifications in non-strict mode.
 *
 * @remarks
 * **Workaround:** To protect against aggressive bundlers (Rollup/Terser) mutating the TS 7.0
 * ES2022 decorator IIFE structure, users must append `return Object.freeze(this) as this;` to their
 * constructors to ensure immutability survives production bundling.
 *
 * @param value - The class constructor to freeze
 * @param context - The decorator context
 * @returns The immutable class wrapper
 * @example
 * ```ts
 * @Immutable
 * class Config { ... }
 * ```
 */
export function Immutable(value, { kind, name, addInitializer, metadata }) {
    const finalName = getClassName(value, name);
    switch (kind) {
        case 'class':
            return createImmutableWrapper(value, finalName, addInitializer, (instance) => { Object.freeze(instance); return instance; }, metadata);
        default:
            throw new Error(`@Immutable decorating unknown 'kind': ${kind} (${name})`);
    }
}
/**
 * A class decorator that registers a class for serialization with the runtime type system.
 *
 * @param value - The class constructor to register
 * @param context - The decorator context
 * @returns The original class constructor
 * @example
 * ```ts
 * @Serializable
 * class DataModel { ... }
 * ```
 */
export function Serializable(value, { kind, name, addInitializer }) {
    const finalName = getClassName(value, name);
    if (finalName)
        registerType(value, finalName);
    switch (kind) {
        case 'class':
            if (finalName)
                addInitializer(() => registerSerializable(finalName, value)); // register the class for serialization, via its toString() method
            return value;
        default:
            throw new Error(`@Serializable decorating unknown 'kind': ${kind} (${name})`);
    }
}
/**
 * A class decorator that prevents instantiation of the class.
 * Useful for grouping static methods together without allowing instances to be created.
 *
 * @param value - The class constructor to make static
 * @param context - The decorator context
 * @returns A wrapper that throws a TypeError when instantiated
 * @example
 * ```ts
 * @Static
 * class MathUtils { ... }
 * ```
 */
export function Static(value, { kind, name }) {
    const finalName = getClassName(value, name);
    switch (kind) {
        case 'class': {
            const safeName = finalName || 'Anonymous';
            const wrapper = {
                [safeName]: class extends value {
                    constructor(...args) {
                        throw new TypeError(`${safeName} is not a constructor`);
                        super(...args);
                    }
                }
            }[safeName];
            if (finalName) {
                registerType(value, `${finalName}_original`); // register the original class definition
                registerType(wrapper, finalName); // register the wrapper as the authoritative definition
            }
            return wrapper;
        }
        default:
            throw new Error(`@Static decorating unknown 'kind': ${kind} (${name})`);
    }
}
/**
 * Applies a string tag to a class and its prototype.
 *
 * Supports direct use as `@StringTag` and configured use as `@StringTag('CustomName')`.
 *
 * @param tagOrValue - A custom tag string or the class constructor when used directly.
 * @param context - The decorator context for direct decorator usage.
 * @returns The decorated class or a decorator that applies the tag.
 */
export function StringTag(tagOrValue, context) {
    const applyTag = (value, customTag, contextName) => {
        const proto = value.prototype;
        const tagName = customTag ?? getClassName(value, contextName);
        if (tagName) {
            if (proto && !hasOwn(proto, Symbol.toStringTag)) {
                Object.defineProperty(proto, Symbol.toStringTag, {
                    value: tagName,
                    configurable: true,
                    writable: false,
                    enumerable: false,
                });
            }
            if (!value.name || value.name !== tagName) {
                try {
                    Object.defineProperty(value, 'name', {
                        value: tagName,
                        configurable: true,
                        writable: false,
                        enumerable: false,
                    });
                }
                catch { }
            }
        }
        return value;
    };
    if (isCallable(tagOrValue) && context?.kind === 'class')
        return applyTag(tagOrValue, undefined, context.name);
    return (value, ctx) => {
        return applyTag(value, isString(tagOrValue) ? tagOrValue : undefined, ctx?.name);
    };
}
/**
 * A class decorator that transforms a class into a Singleton pattern.
 * Intercepts constructor calls to return a cached instance upon subsequent instantiations.
 * Also attaches a static `.instance` property to the decorated constructor.
 *
 * @param targetOrOptions - Class constructor or Singleton options
 * @param context - Optional decorator context (when used without parentheses)
 * @example
 * ```ts
 *  @ Singleton
 *  class ConfigStore {
 *    public apiKey = 'secret_123';
 *  }
 *
 *  const a = new ConfigStore();
 *  const b = new ConfigStore();
 *  console.log(a === b); // true
 *  console.log((ConfigStore as any).instance === a); // true
 * ```
 */
export function Singleton(targetOrOptions, context) {
    const decorate = (value, options) => {
        let instance;
        let initialArgs;
        const safeName = value.name || 'Singleton';
        const wrapper = {
            [safeName]: class extends value {
                constructor(...args) {
                    if (instance) {
                        if (options?.allowArgMismatch !== true && initialArgs) {
                            const prev = initialArgs;
                            const isMismatch = args.length !== prev.length || args.some((arg, i) => arg !== prev[i]);
                            if (isMismatch)
                                throw new Error(`[Singleton] Argument mismatch on subsequent instantiation of '${safeName}'`);
                        }
                        return instance;
                    }
                    super(...args);
                    initialArgs = args;
                    instance = this;
                    return this;
                }
            }
        }[safeName];
        Object.defineProperty(wrapper, 'instance', {
            get: () => instance,
            configurable: true,
            enumerable: false,
        });
        return wrapper;
    };
    if (isCallable(targetOrOptions) && context?.kind === 'class') {
        return decorate(targetOrOptions);
    }
    return (value, _ctx) => {
        return decorate(value, targetOrOptions);
    };
}
/**
 * A method, property, or getter decorator that marks a class member to remain mutable
 * when the class is decorated with `@Immutable` or `@Securable`.
 *
 * @param condition - Optional boolean or predicate function. If provided, the member remains mutable ONLY when condition evaluates to true.
 * @example
 * ```ts
 *  @ Mutable()
 *  static cache;
 *
 *  @ Mutable(() => isTestEnvironment())
 *  static init() { ... }
 * ```
 */
export function Mutable(targetOrCondition, context) {
    const applyMutable = (ctx, condition) => {
        const entry = {
            name: ctx.name,
            condition: isCallable(condition) ? condition : () => (condition ?? true),
        };
        if (ctx.metadata) {
            let list = ctx.metadata[$Mutable];
            if (!Array.isArray(list)) {
                list = [];
                Object.defineProperty(ctx.metadata, $Mutable, { value: list, writable: true, configurable: true });
            }
            list.push(entry);
        }
        ctx.addInitializer(function () {
            const targets = [this];
            if (this && isCallable(this.constructor) && this.constructor !== this) {
                targets.push(this.constructor);
            }
            for (const target of targets) {
                let list = target[$Mutable];
                if (!Array.isArray(list)) {
                    list = [];
                    Object.defineProperty(target, $Mutable, { value: list, writable: true, configurable: true });
                }
                list.push(entry);
            }
        });
    };
    if (hasKey(context, 'kind'))
        return applyMutable(context, true);
    return (_value, ctx) => {
        applyMutable(ctx, targetOrCondition);
    };
}
